<template>
  <v-card
    id="screenContent"
    class="vetalfa-view"
    :max-width="_getMaxWidth()"
    flat
  >
    <div class="vetalfa-global-header" ref="screenHeader">
      <vetalfa-small-header
        showBreadCrumbAndTitle
        :title="_getLabel('ENCOUNTER')"
        :breadcrumb="_getLabel('ENCOUNTER')"
      ></vetalfa-small-header>
      <v-row no-gutters justify="center">
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col cols="12" class="px-4 my-2" v-if="patient">
            <v-card flat class="vetalfa-card-grey-border pa-3 clinical-patient-summary">
              <v-row no-gutters align="center" justify="space-between">
                <v-col>
                  <p class="subtitle-2 font-weight-bold ma-0">{{ patient.name }}</p>
                  <p class="caption grey--text text--darken-1 ma-0">
                    {{
                      animals.getAnimalShortDescription(patient.species) +
                      " • " +
                      patient.breed
                    }}
                  </p>
                </v-col>
                <v-col cols="auto" v-if="encounter && isFinished">
                  <v-chip small color="secondary" class="primary--text">
                    <v-icon size="14" class="mr-1">{{ mdiLockOutline }}</v-icon>
                    {{ formattedDate }}
                  </v-chip>
                </v-col>
                <v-col cols="auto" v-else-if="encounter">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    left
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        color="secondary"
                        class="primary--text vetalfa-pointer"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon size="14" class="mr-1">{{ mdiCalendarEdit }}</v-icon>
                        {{ formattedDate }}
                      </v-chip>
                    </template>
                    <v-date-picker
                      v-model="encounterDate"
                      @input="dateMenu = false"
                      :max="todayDate"
                      no-title
                      locale="pt-br"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-row>
      <v-row no-gutters justify="center" v-if="isFinished">
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col cols="12" class="px-4 mb-2">
            <v-alert
              dense
              outlined
              color="success"
              class="mb-0"
              :icon="mdiLockOutline"
            >
              <p class="subtitle-2 ma-0">{{ _getLabel('FINISHED') }}</p>
              <p class="caption ma-0">
                {{ _getLabel('ENCOUNTER_FINALIZED_BY') }}: {{ finalizedByText }}
              </p>
              <p class="caption ma-0" v-if="finalizedAtText">
                {{ finalizedAtText }}
              </p>
            </v-alert>
          </v-col>
        </v-row>
      </v-row>
      <v-row no-gutters justify="center" v-if="encounter">
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col
            :cols="_isMobile() ? 12 : 'auto'"
            class="px-4 clinical-encounter-tabs"
          >
            <v-tabs
              v-model="encounterTab"
              active-class="primary--text font-weight-bold"
              slider-color="primary"
              show-arrows
              class="ma-0 pa-0"
            >
              <v-tab class="text-none">{{ _getLabel('ANAMNESIS') }}</v-tab>
              <v-tab class="text-none">{{ _getLabel('PHYSICAL_EXAM') }}</v-tab>
              <v-tab class="text-none">{{ _getLabel('DIAGNOSIS') }}</v-tab>
              <v-tab class="text-none">{{ _getLabel('PRESCRIPTIONS') }}</v-tab>
            </v-tabs>
          </v-col>
          <v-spacer v-if="!_isMobile()"></v-spacer>
          <v-col
            v-if="!_isMobile() && !contextLoading && !contextError"
            cols="auto"
            class="clinical-encounter-desktop-actions d-flex align-center px-4"
          >
            <template v-if="!isFinished">
              <v-btn
                rounded
                outlined
                color="primary"
                class="text-none"
                :loading="saving"
                :disabled="prescriptionSelectionBusy"
                @click="saveAllFromButton"
              >
                <v-icon size="18" class="mr-2">{{ mdiContentSaveOutline }}</v-icon>
                Salvar rascunho
              </v-btn>
              <v-btn
                rounded
                depressed
                color="primary"
                class="text-none white--text"
                :loading="saving"
                :disabled="prescriptionSelectionBusy"
                @click="finalize"
              >
                <v-icon size="18" class="mr-2">{{ mdiCheckCircleOutline }}</v-icon>
                {{ _getLabel('FINALIZE_ENCOUNTER') }}
              </v-btn>
            </template>
            <v-btn
              v-else
              rounded
              outlined
              color="primary"
              class="text-none"
              :loading="saving"
              @click="openAddendumDialog"
            >
              <v-icon size="18" class="mr-2">{{ mdiNotePlusOutline }}</v-icon>
              {{ _getLabel('ADD_ADDENDUM') }}
            </v-btn>
          </v-col>
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
          <v-col
            v-if="showDraftStatus"
            cols="12"
            class="px-4 py-2"
          >
            <div
              class="clinical-draft-status d-flex align-center caption"
              :class="draftStatusTextClass"
              aria-live="polite"
            >
              <v-progress-circular
                v-if="saving || ['pending', 'saving'].includes(draftSaveState)"
                indeterminate
                size="14"
                width="2"
                color="warning"
                class="mr-2"
              />
              <v-icon v-else size="16" color="#B42318" class="mr-2">
                {{ mdiAlertCircleOutline }}
              </v-icon>
              {{ draftStatusText }}
            </div>
          </v-col>
        </v-row>
      </v-row>
    </div>

    <div ref="screenContent">
      <v-row no-gutters justify="center">
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col cols="12" class="px-4">
            <v-alert v-if="contextError" outlined color="error" class="mt-4 mb-0">
              <v-row no-gutters align="center">
                <v-col>{{ contextError }}</v-col>
                <v-col cols="auto"><v-btn small text color="primary" class="text-none" @click="loadContext">Tentar novamente</v-btn></v-col>
              </v-row>
            </v-alert>
            <div v-if="contextLoading" class="clinical-context-loading mt-8 text-center">
              <v-progress-circular indeterminate color="primary" size="28" />
            </div>
            <v-form v-if="!contextLoading && !contextError" :disabled="saving" @submit.prevent>
              <v-row v-if="encounter && encounterTab === 0" no-gutters class="mt-4">
                <v-col cols="12">
                  <div v-if="isFinished" class="clinical-read-block">
                    <p v-if="localEncounter.chiefComplaint"><span>{{ _getLabel('CHIEF_COMPLAINT') }}</span>{{ localEncounter.chiefComplaint }}</p>
                    <p v-if="localEncounter.notes"><span>{{ _getLabel('NOTES') }}</span>{{ localEncounter.notes }}</p>
                  </div>
                  <v-text-field
                    v-else
                    v-model="localEncounter.chiefComplaint"
                    outlined
                    dense
                    :label="_getLabel('CHIEF_COMPLAINT')"
                  ></v-text-field>
                  <v-textarea
                    v-if="!isFinished"
                    v-model="localEncounter.notes"
                    outlined
                    dense
                    :label="_getLabel('NOTES')"
                    rows="2"
                  ></v-textarea>
                  <anamnesis-form v-model="localAnamnesis" :readonly="isFinished" />
                </v-col>
              </v-row>

              <v-row v-if="encounter && encounterTab === 1" no-gutters class="mt-4">
                <v-col cols="12">
                  <physical-exam-form
                    v-model="localPhysicalExam"
                    :species="patient ? patient.species : null"
                    :readonly="isFinished"
                  />
                </v-col>
              </v-row>

              <v-row v-if="encounter && encounterTab === 2" no-gutters class="mt-4">
                <v-col cols="12">
                  <diagnosis-form
                    v-model="localDiagnoses"
                    :encounterId="encounter ? encounter.id : null"
                    :readonly="isFinished"
                  />
                </v-col>
              </v-row>
            </v-form>

            <v-row v-if="encounter && !contextLoading && !contextError && encounterTab === 3" no-gutters class="mt-4">
              <v-col cols="12">
                <prescription-builder
                  ref="prescriptionBuilder"
                  embedded
                  :readonly="isFinished || saving"
                  :patientId="routePatientId"
                  :encounterId="routeEncounterId"
                  :skipMedicalCheck="true"
                  :showPatients="false"
                  :selectionDisabled="saving"
                  @context-selected="handlePrescriptionContextSelected"
                  @selection-busy="handlePrescriptionSelectionBusy"
                />
              </v-col>
            </v-row>

            <v-row v-if="isFinished" no-gutters class="mt-6">
              <v-col cols="12">
                <v-divider class="mb-3"></v-divider>
                <p class="subtitle-2 font-weight-bold ma-0 mb-2">
                  {{ _getLabel('ADDENDA') }}
                </p>
                <p
                  v-if="!addenda.length"
                  class="caption grey--text text--darken-1 ma-0"
                >
                  {{ _getLabel('NO_ADDENDA') }}
                </p>
                <v-card
                  v-for="(item, index) in addenda"
                  :key="`addendum-${index}`"
                  flat
                  class="vetalfa-card-grey-border pa-3 mb-3"
                >
                  <p class="body-2 ma-0" style="white-space: pre-wrap">
                    {{ item.text }}
                  </p>
                  <p class="caption grey--text text--darken-1 ma-0 mt-2">
                    {{ getAddendumSignature(item) }}
                  </p>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-row>

      <v-row no-gutters style="height: 100px"></v-row>
    </div>

    <v-row
      v-if="_isMobile() && encounter && !contextLoading && !contextError"
      ref="screenFooter"
      no-gutters
      justify="center"
      class="vetalfa-button-bottom-combo clinical-encounter-actions white px-4"
    >
      <template v-if="!isFinished">
        <v-col
          :cols="_xsOnly() ? 5 : 'auto'"
          :class="{ 'mr-3': !_xsOnly() }"
        >
          <v-btn
            rounded
            outlined
            color="primary"
            class="text-none"
            :block="_xsOnly()"
            :loading="saving"
            :disabled="prescriptionSelectionBusy"
            @click="saveAllFromButton"
          >
            Salvar rascunho
          </v-btn>
        </v-col>
        <v-col :cols="_xsOnly() ? 7 : 'auto'" :class="{ 'pl-2': _xsOnly() }">
          <v-btn
            rounded
            color="primary"
            class="text-none"
            :block="_xsOnly()"
            :loading="saving"
            :disabled="prescriptionSelectionBusy"
            @click="finalize"
          >
            {{ _getLabel('FINALIZE_ENCOUNTER') }}
          </v-btn>
        </v-col>
      </template>
      <v-col v-else :cols="_xsOnly() ? 12 : 'auto'">
        <v-btn
          rounded
          outlined
          color="primary"
          class="text-none"
          :block="_xsOnly()"
          :loading="saving"
          @click="openAddendumDialog"
        >
          <v-icon size="20" class="mr-2">{{ mdiNotePlusOutline }}</v-icon>
          {{ _getLabel('ADD_ADDENDUM') }}
        </v-btn>
      </v-col>
    </v-row>

    <vetalfa-dialog
      :toggleModel="addendumDialog"
      :bottom="_xsOnly()"
      :fullscreen="false"
      :height="_getDialogHeight(0.75)"
      max-width="600px"
      :smallHeader="false"
      :featureMode="false"
      :dialogHeader="{ title: _getLabel('ADD_ADDENDUM') }"
      persistent
      contentClasses="clinical-record-bottom-sheet"
      @dialogClose="closeAddendumDialog"
    >
      <div class="px-4 pt-3">
        <p class="caption grey--text text--darken-1 ma-0 mb-3">
          {{ _getLabel('ADDENDUM_HINT') }}
        </p>
        <v-textarea
          v-model="addendumText"
          outlined
          dense
          rows="5"
          :label="_getLabel('ADDENDUM_TEXT')"
        ></v-textarea>
      </div>
      <template #actions>
        <div class="clinical-dialog-actions px-4 pb-4">
          <v-btn
            rounded
            color="primary"
            class="text-none"
            block
            :disabled="!addendumText || !addendumText.trim()"
            :loading="savingAddendum"
            @click="submitAddendum"
          >
            {{ _getLabel('SAVE') }}
          </v-btn>
        </div>
      </template>
    </vetalfa-dialog>
    <clinical-draft-dialog
      :value="draftDialog"
      :recovery="!!recoveryDraft"
      :busy="saving"
      :error="draftError"
      entityLabel="consulta"
      @restore="restoreDraft"
      @discard="handleDraftDiscard"
      @keep="keepAndLeave"
      @cancel="cancelLeave"
    />
  </v-card>
</template>

<style scoped>
.clinical-patient-summary { background: #f5f6fa; border-color: #e5e9f1 !important; }
.clinical-read-block { padding: 4px 0 16px; }
.clinical-read-block p { color:#193153; white-space:pre-wrap; margin:0 0 14px; }
.clinical-read-block span { color:#667085; display:block; font-size:12px; font-weight:700; margin-bottom:4px; }
.clinical-context-loading { min-height:160px; }
.clinical-draft-status { min-height: 20px; line-height: 16px; }
.clinical-draft-status--error { color: #B42318 !important; }
.clinical-dialog-actions { width: 100%; }
@media (max-width: 599px) {
  .vetalfa-button-bottom-combo { padding-bottom: calc(12px + env(safe-area-inset-bottom)); }
  .clinical-dialog-actions {
    padding-left: 20px !important;
    padding-right: 20px !important;
    padding-bottom: max(20px, env(safe-area-inset-bottom)) !important;
  }
}
</style>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import AnamnesisForm from "@/components/AnamnesisForm.vue";
import PhysicalExamForm from "@/components/PhysicalExamForm.vue";
import DiagnosisForm from "@/components/DiagnosisForm.vue";
import PrescriptionBuilder from "@/components/PrescriptionBuilder.vue";
import ClinicalDraftDialog from "@/components/ClinicalDraftDialog.vue";
import clinicalDrafts from "@/services/clinical-drafts.js";
import clinicalLayout from "@/mixin/clinical-layout.js";
import animals from "@/constants/animals.enum.js";
import {
  timestampToDateInput,
  dateInputToTimestamp,
} from "@/constants/records.utils.js";
import {
  mdiAlertCircleOutline,
  mdiCalendarEdit,
  mdiCheckCircleOutline,
  mdiContentSaveOutline,
  mdiLockOutline,
  mdiNotePlusOutline,
} from "@mdi/js";

export default {
  name: "EncounterDetail",
  mixins: [clinicalLayout],
  components: {
    "anamnesis-form": AnamnesisForm,
    "physical-exam-form": PhysicalExamForm,
    "diagnosis-form": DiagnosisForm,
    "prescription-builder": PrescriptionBuilder,
    "clinical-draft-dialog": ClinicalDraftDialog,
  },
  props: {
    patientId: {
      type: String,
      default: null,
      required: false,
    },
    encounterId: {
      type: String,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      animals,
      mdiAlertCircleOutline,
      mdiCalendarEdit,
      mdiCheckCircleOutline,
      mdiContentSaveOutline,
      mdiLockOutline,
      mdiNotePlusOutline,
      encounterTab: 0,
      dateMenu: false,
      saving: false,
      addendumDialog: false,
      addendumText: "",
      savingAddendum: false,
      leavingConfirmed: false,
      contextLoading: true,
      contextError: '',
      contextSequence: 0,
      draftDialog: false,
      draftError: '',
      recoveryDraft: null,
      draftSaveState: 'account',
      draftSavedAt: null,
      draftTimer: null,
      draftWritesDisabled: false,
      accountSaveError: false,
      prescriptionSelectionBusy: false,
      prescriptionBaseline: '[]',
      localAnamnesis: {},
      localPhysicalExam: {},
      localDiagnoses: [],
      localEncounter: {},
    };
  },
  computed: {
    ...mapGetters("patients", ["patient"]),
    ...mapGetters("general", ["toggles", "user"]),
    ...mapGetters("prescriptions", [
      "prescription",
      "prescriptionId",
      "prescriptionRevision",
      "shareDate",
      "files",
      "professionalSnapshot",
    ]),
    ...mapGetters("records", [
      "selectedEncounter",
      "anamnesis",
      "physicalExam",
      "diagnoses",
    ]),
    encounter() {
      return this.selectedEncounter;
    },
    routePatientId() {
      return this.patientId || this.$route.params.patientId || null;
    },
    routeEncounterId() {
      return this.encounterId || this.$route.params.encounterId || null;
    },
    hasRecordsToggle() {
      return !!(this.toggles && this.toggles["patient_records"]);
    },
    hasPrescriptionsToggle() {
      return !!(this.toggles && this.toggles["prescriptions"]);
    },
    /**
     * A aba de prescricao desta tela e a unica forma de criar uma prescricao no
     * app, entao o guard nao pode derrubar quem tem so `prescriptions` ligado
     * (app mobile e organizacoes fora do piloto do prontuario, onde
     * `patient_records` e sempre falso). So quem nao tem nenhum dos dois volta
     * para a home.
     */
    canAccessRoute() {
      return this.hasRecordsToggle;
    },
    /**
     * Deriva do estado do servidor (`finalizedAt`), nunca de um status mutado
     * localmente, para que a tela so trave depois da confirmacao do backend.
     */
    isFinished() {
      return !!(this.encounter && this.encounter.finalizedAt);
    },
    finalizedByText() {
      const author = (this.encounter && this.encounter.finalizedBy) || {};
      const name = [author.displayNamePrefix, author.displayName]
        .filter((part) => !!part)
        .join(" ");
      const crmv =
        author.crmvNumber || author.crmvState
          ? `CRMV ${author.crmvNumber || ""}${
              author.crmvState ? "-" + author.crmvState : ""
            }`.trim()
          : "";
      return [name, crmv].filter((part) => !!part).join(" • ") || "-";
    },
    finalizedAtText() {
      const ts = this.encounter && this.encounter.finalizedAt;
      if (!ts) return "";
      return new Date(ts).toLocaleString("pt-BR");
    },
    addenda() {
      return (this.encounter && this.encounter.addenda) || [];
    },
    isDirty() {
      if (this.draftWritesDisabled || this.contextLoading || this.contextError || !this.encounter || this.isFinished) return false;
      return (
        JSON.stringify(this.prescription || []) !== this.prescriptionBaseline ||
        JSON.stringify(this.localEncounter) !==
          JSON.stringify(this.encounter || {}) ||
        JSON.stringify(this.localAnamnesis) !==
          JSON.stringify(this.anamnesis || {}) ||
        JSON.stringify(this.localPhysicalExam) !==
          JSON.stringify(this.physicalExam || {}) ||
        JSON.stringify(this.normalizeDiagnoses(this.localDiagnoses)) !==
          JSON.stringify(this.normalizeDiagnoses(this.diagnoses))
      );
    },
    draftContext() {
      return {
        userId: this.user && this.user.uid,
        organizationId: (this.user && this.user.organizationId) || null,
        patientId: this.routePatientId,
        encounterId: this.routeEncounterId,
      };
    },
    draftContents() {
      return { encounter: this.localEncounter, anamnesis: this.localAnamnesis, physicalExam: this.localPhysicalExam,
        diagnoses: this.localDiagnoses, prescription: this.prescription, prescriptionId: this.prescriptionId,
        prescriptionRevision: this.prescriptionRevision, prescriptionShareDate: this.shareDate,
        prescriptionFiles: this.files, prescriptionProfessionalSnapshot: this.professionalSnapshot };
    },
    showDraftStatus() {
      if (this.isFinished || this.contextLoading || this.contextError) return false;
      return !!(
        this.saving ||
        this.accountSaveError ||
        ["pending", "saving", "error"].includes(this.draftSaveState)
      );
    },
    draftStatusTextClass() {
      return this.draftSaveState === "error" || this.accountSaveError
        ? "clinical-draft-status--error"
        : "grey--text text--darken-1";
    },
    draftStatusText() {
      const time = this.draftSavedAt
        ? ` às ${new Date(this.draftSavedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
        : '';
      if (this.saving) return 'Salvando na conta…';
      if (this.draftSaveState === 'pending') return 'Alterações pendentes neste dispositivo.';
      if (this.draftSaveState === 'saving') return 'Guardando uma cópia neste dispositivo…';
      if (this.draftSaveState === 'error' && this.accountSaveError) return 'Falha ao salvar na conta e ao guardar a cópia neste dispositivo.';
      if (this.draftSaveState === 'error') return 'Falha ao guardar a cópia neste dispositivo. Salve na conta antes de sair.';
      if (this.accountSaveError && this.draftSaveState === 'local') return 'Falha ao salvar na conta. Cópia guardada neste dispositivo.';
      if (this.accountSaveError) return 'Falha ao salvar na conta. Suas alterações continuam abertas.';
      if (this.draftSaveState === 'local') return `Cópia guardada neste dispositivo${time}.`;
      return `Dados salvos na conta${time}.`;
    },
    formattedDate() {
      const ts =
        this.localEncounter.date ||
        this.localEncounter.lastUpdate ||
        this.localEncounter.createdAt;
      if (!ts) return "-";
      return this._getDateFromTimestamp(ts);
    },
    encounterDate: {
      get() {
        return timestampToDateInput(
          this.localEncounter.date ||
            this.localEncounter.lastUpdate ||
            this.localEncounter.createdAt
        );
      },
      set(val) {
        const timestamp = dateInputToTimestamp(val);
        if (!timestamp) return;
        // `dateInputToTimestamp` grava meio-dia local, que ainda e futuro quando
        // o dia escolhido e hoje e ainda nao deu meio-dia. O backend rejeita data
        // clinica futura com 400, entao o valor e limitado ao agora.
        this.localEncounter = {
          ...this.localEncounter,
          date: Math.min(timestamp, Date.now()),
        };
      },
    },
    todayDate() {
      return timestampToDateInput(Date.now());
    },
  },
  methods: {
    ...mapMutations("general", ["setConfirmModel", "setAlert"]),
    ...mapActions("general", [
      "routeRedirect",
      "routePathRedirect",
      "showAlert",
      "showMessage",
    ]),
    ...mapActions("patients", ["selectPatientById"]),
    ...mapActions("prescriptions", ["updateDraftFromPrescriptionAction", "initializeEncounterPrescriptionAction"]),
    ...mapActions("records", [
      "fetchEncounter",
      "fetchEncounterContext",
      "fetchAnamnesis",
      "fetchPhysicalExam",
      "fetchDiagnoses",
      "saveEncounter",
      "saveAnamnesis",
      "savePhysicalExam",
      "saveDiagnoses",
      "addEncounterAddendum",
    ]),
    async loadContext() {
      this.cancelDraftTimer();
      this.prescriptionSelectionBusy = false;
      const sequence = ++this.contextSequence;
      this.contextLoading = true;
      this.contextError = '';
      this.localEncounter = {};
      this.localAnamnesis = {};
      this.localPhysicalExam = {};
      this.localDiagnoses = [];
      this.recoveryDraft = null;
      this.draftDialog = false;
      this.draftSaveState = 'account';
      this.draftSavedAt = null;
      this.accountSaveError = false;
      try {
        const resolved = await this.resolvePatientFromRoute();
        if (!resolved || sequence !== this.contextSequence) return;
        const result = await this.fetchEncounterContext({ patientId: this.routePatientId, encounterId: this.routeEncounterId });
        if (sequence !== this.contextSequence) return;
        if (!result || !result.ok) throw new Error(this.getServerMessage(result && result.error) || 'Não foi possível carregar esta consulta. Tente novamente.');
        const recipe = await this.initializeEncounterPrescriptionAction({ patientId: this.routePatientId, encounterId: this.routeEncounterId, readonly: this.isFinished });
        if (sequence !== this.contextSequence) return;
        if (!recipe || !recipe.ok) throw new Error('Não foi possível carregar a prescrição vinculada. Tente novamente.');
        await this.$nextTick();
        this.prescriptionBaseline = JSON.stringify(this.prescription || []);
        const draft = await clinicalDrafts.load(this.draftContext);
        if (sequence !== this.contextSequence) return;
        if (this.isFinished) await clinicalDrafts.remove(this.draftContext);
        else if (draft && draft.invalid) this.showAlert({ text: 'A cópia local estava indisponível. Os dados salvos no servidor foram carregados.', color: 'warning' });
        else if (draft) {
          this.recoveryDraft = draft;
          this.draftSaveState = 'local';
          this.draftSavedAt = draft.savedAt;
          this.draftDialog = true;
        }
      } catch (error) {
        if (sequence === this.contextSequence) this.contextError = error.message || 'Não foi possível abrir a consulta.';
      } finally {
        if (sequence === this.contextSequence) {
          this.contextLoading = false;
          this.$nextTick(() => this._setupScreenSizes(this));
        }
      }
    },
    cancelDraftTimer() {
      if (this.draftTimer) clearTimeout(this.draftTimer);
      this.draftTimer = null;
    },
    scheduleDraftPersistence() {
      if (this.draftWritesDisabled || this.contextLoading || this.saving || this.recoveryDraft || !this.isDirty) return;
      this.cancelDraftTimer();
      this.draftSaveState = 'pending';
      this.draftTimer = setTimeout(() => {
        this.draftTimer = null;
        this.persistDraft();
      }, 600);
    },
    async persistDraft() {
      this.cancelDraftTimer();
      if (this.draftWritesDisabled || !this.isDirty || this.recoveryDraft) return true;
      const context = { ...this.draftContext };
      const contextToken = JSON.stringify(context);
      const contents = JSON.parse(JSON.stringify(this.draftContents));
      this.draftSaveState = 'saving';
      try {
        await clinicalDrafts.save(context, contents);
        if (contextToken === JSON.stringify(this.draftContext) && !this.draftWritesDisabled) {
          this.draftSaveState = 'local';
          this.draftSavedAt = Date.now();
          this.draftError = '';
        }
        return true;
      } catch {
        if (contextToken === JSON.stringify(this.draftContext) && !this.draftWritesDisabled) {
          this.draftSaveState = 'error';
          this.draftError = 'Não foi possível guardar a cópia local. Salve online antes de sair.';
        }
        return false;
      }
    },
    flushDraft() {
      return this.persistDraft();
    },
    handleLogout() {
      this.draftWritesDisabled = true;
      this.cancelDraftTimer();
    },
    handleLogoutCancelled() {
      this.draftWritesDisabled = false;
      this.scheduleDraftPersistence();
    },
    async handlePrescriptionContextSelected({ pages = [], discardedUnsaved = false } = {}) {
      this.cancelDraftTimer();
      this.prescriptionBaseline = JSON.stringify(pages || []);
      if (this.isDirty) {
        // The selected prescription is already B. Persist immediately so an
        // older queued write for A is followed by the current complete snapshot.
        await this.persistDraft();
        return;
      }
      if (discardedUnsaved) {
        try {
          // Delete is serialized behind any in-flight write for the same key,
          // so the discarded prescription cannot reappear after navigation.
          await clinicalDrafts.remove(this.draftContext);
        } catch {
          this.draftSaveState = 'error';
          this.draftError = 'Não foi possível descartar a cópia local anterior.';
          return;
        }
      }
      this.accountSaveError = false;
      if (discardedUnsaved || ['pending', 'saving'].includes(this.draftSaveState)) {
        this.draftSaveState = 'account';
        this.draftSavedAt = null;
        this.draftError = '';
      }
    },
    handlePrescriptionSelectionBusy(value) {
      this.prescriptionSelectionBusy = !!value;
    },
    async restoreDraft() {
      const saved = this.recoveryDraft && this.recoveryDraft.data;
      if (!saved) return;
      // Preserve the original revision so a newer server edit produces a conflict,
      // never an implicit overwrite of another professional's work.
      this.localEncounter = saved.encounter || {};
      this.localAnamnesis = saved.anamnesis || {};
      this.localPhysicalExam = saved.physicalExam || {};
      this.localDiagnoses = saved.diagnoses || [];
      const restored = await this.initializeEncounterPrescriptionAction({ patientId: this.routePatientId, encounterId: this.routeEncounterId,
        prescriptionId: saved.prescriptionId, prescription: { id: saved.prescriptionId, revision: saved.prescriptionRevision,
          pages: saved.prescription || [], patientId: this.routePatientId, encounterId: this.routeEncounterId,
          shareDate: saved.prescriptionShareDate || null, files: saved.prescriptionFiles || [],
          professionalSnapshot: saved.prescriptionProfessionalSnapshot || null } });
      if (!restored || !restored.ok) { this.draftError = 'Não foi possível recuperar a receita. A cópia local foi preservada.'; return; }
      this.recoveryDraft = null;
      this.draftDialog = false;
      this.draftSaveState = 'local';
      this.draftSavedAt = Date.now();
    },
    async discardDraft() {
      try {
        await clinicalDrafts.remove(this.draftContext);
        this.recoveryDraft = null;
        this.draftDialog = false;
        this.draftSaveState = 'account';
        this.draftSavedAt = null;
      }
      catch { this.draftError = 'Não foi possível descartar a cópia local.'; }
    },
    requestLeave(next) {
      if (this.prescriptionSelectionBusy) {
        this.showAlert({ text: 'Aguarde a receita terminar de carregar antes de sair.', color: 'warning' });
        return next(false);
      }
      if (this.saving) return next(false);
      if (this.contextLoading) { ++this.contextSequence; return next(); }
      if (this.isFinished || !this.isDirty) return next();
      this._pendingNavigation = next;
      this.draftError = '';
      this.draftDialog = true;
    },
    cancelLeave() { this.draftDialog = false; if (this._pendingNavigation) this._pendingNavigation(false); this._pendingNavigation = null; },
    finishLeave() { this.draftDialog = false; const next = this._pendingNavigation; this._pendingNavigation = null; if (next) next(); },
    async keepAndLeave() { if (await this.flushDraft()) this.finishLeave(); },
    handleDraftDiscard() {
      if (this.recoveryDraft) return this.discardDraft();
      return this.discardAndLeave();
    },
    async discardAndLeave() {
      this.cancelDraftTimer();
      try {
        await clinicalDrafts.remove(this.draftContext);
        this.draftError = "";
        this.finishLeave();
      } catch {
        this.draftError = "Não foi possível descartar a cópia local.";
      }
    },
    /**
     * Espelha a normalizacao do DiagnosisForm para que apenas a abertura da aba
     * de diagnosticos nao marque a consulta como alterada.
     */
    normalizeDiagnoses(list) {
      return (list || []).map((item) => ({
        id: item.id || null,
        name: item.name || "",
        type: typeof item.type === "number" ? item.type : 1,
        status: typeof item.status === "number" ? item.status : 0,
        notes: item.notes || "",
        deleted: item.deleted || null,
      }));
    },
    /**
     * Monta o corpo do save removendo os campos que so o servidor controla.
     */
    buildEncounterPayload(extra = {}) {
      const payload = { ...this.localEncounter, ...extra };
      delete payload.status;
      delete payload.finalizedAt;
      delete payload.finalizedBy;
      delete payload.addenda;
      delete payload.createdAt;
      delete payload.lastUpdate;
      payload.expectedRevision = payload.revision || 0;
      payload.expectedContentRevision = payload.contentRevision || 0;
      delete payload.revision;
      delete payload.contentRevision;
      return payload;
    },
    /**
     * Persiste o rascunho da prescricao da aba 4. Sem itens nao ha o que salvar.
     * O `loadingHandler` nao propaga erro: a action so reescreve `prescription` e
     * `prescriptionId` com a resposta do servidor, entao o sucesso e conferido
     * pela troca dessas referencias. Olhar so para `prescriptionId` daria falso
     * positivo ao re-salvar um rascunho que ja tinha id.
     */
    async savePrescriptionDraft() {
      const pages = this.prescription || [];
      const hasItems = pages.some((page) => (page || []).length > 0);
      if ((!hasItems && !this.prescriptionId) || !this.patient) return { ok: true };
      return await this.updateDraftFromPrescriptionAction();
    },
    /**
     * Salva todas as secoes da consulta e devolve { ok, failed }.
     * Nenhuma secao mascara a falha da outra: as que retornarem ok: false sao
     * listadas em um unico alerta, e a consulta nunca e tratada como salva.
     */
    async saveAll({ showAlerts = true, showSuccess = false, alertSuffix = "" } = {}) {
      if (this.prescriptionSelectionBusy) {
        if (showAlerts) this.showAlert({ text: 'Aguarde a receita terminar de carregar antes de salvar.', color: 'warning' });
        return { ok: false, failed: [this._getLabel('PRESCRIPTIONS')] };
      }
      if (this.isFinished || this.saving || this.contextLoading || this.contextError) return { ok: false, failed: [] };
      this.accountSaveError = false;
      // Um novo salvamento inicia um novo ciclo de feedback: o timer de um erro
      // anterior não pode fechar a confirmação de sucesso desta tentativa.
      this.setAlert({ active: false, color: '', text: '', icon: '' });
      if (!navigator.onLine) {
        await this.persistDraft();
        this.showAlert({ text: 'Você está offline. O rascunho fica neste dispositivo; conecte-se para salvar ou finalizar.', color: 'warning' });
        return { ok: false, failed: ['Sem conexão'] };
      }
      const encounterId = this.routeEncounterId;
      if (!encounterId) return { ok: false, failed: [] };
      this.saving = true;
      try {
        const results = [];
        results.push({
          name: this._getLabel("ENCOUNTER"),
          result: await this.saveEncounter({
            encounter: this.buildEncounterPayload(),
            showMessage: false,
            showError: false,
          }),
        });
        if (!results[0].result || !results[0].result.ok) {
          const message = this.getServerMessage(results[0].result && results[0].result.error);
          this.showAlert({ text: message || 'Não foi possível salvar a consulta. Suas alterações foram mantidas.', color: 'error' });
          await this.persistDraft();
          this.accountSaveError = true;
          return { ok: false, failed: [this._getLabel('ENCOUNTER')] };
        }
        results.push({
          name: this._getLabel("ANAMNESIS"),
          result: await this.saveAnamnesis({
            encounterId,
            anamnesis: this.localAnamnesis,
            showMessage: false,
            showError: false,
          }),
        });
        results.push({
          name: this._getLabel("PHYSICAL_EXAM"),
          result: await this.savePhysicalExam({
            encounterId,
            physicalExam: this.localPhysicalExam,
            showMessage: false,
            showError: false,
          }),
        });
        results.push({
          name: this._getLabel("DIAGNOSIS"),
          result: await this.saveDiagnoses({
            encounterId,
            diagnoses: this.localDiagnoses,
            showMessage: false,
            showError: false,
          }),
        });
        results.push({
          name: this._getLabel("PRESCRIPTIONS"),
          result: await this.savePrescriptionDraft(),
        });
        const failures = results.filter(({ result }) => !result || !result.ok);
        const failed = failures.map(({ name }) => name);
        if (failed.length && showAlerts) {
          const suffix = alertSuffix ? ` ${alertSuffix}` : "";
          // O 400 do backend explica o motivo (data invalida, campo faltando);
          // sem ele o usuario so ve o nome da secao e repete a acao sem sucesso.
          const detail = failures
            .map(({ result }) => this.getServerMessage(result && result.error))
            .find((message) => !!message);
          const reason = detail ? ` ${detail}` : "";
          this.showAlert({
            text: `${this._getLabel(
              "ENCOUNTER_UNSAVED_SECTIONS"
            )} ${failed.join(", ")}.${reason}${suffix}`,
            color: "error",
          });
        } else if (!failed.length && showSuccess) {
          this.showMessage();
        }
        if (!failed.length) {
          this.cancelDraftTimer();
          this.prescriptionBaseline = JSON.stringify(this.prescription || []);
          await clinicalDrafts.remove(this.draftContext);
          this.draftSaveState = 'account';
          this.draftSavedAt = Date.now();
          this.draftError = '';
        } else {
          await this.persistDraft();
          this.accountSaveError = true;
        }
        return { ok: failed.length === 0, failed };
      } finally {
        this.saving = false;
      }
    },
    async saveAllFromButton() {
      await this.saveAll({ showSuccess: true });
    },
    finalize() {
      if (this.prescriptionSelectionBusy) {
        this.showAlert({ text: 'Aguarde a receita terminar de carregar antes de finalizar.', color: 'warning' });
        return;
      }
      if (this.isFinished || this.saving) return;
      this.setConfirmModel({
        text: this.getFinalizationReviewText(),
        confirmText: this._getLabel("FINALIZE_ENCOUNTER_CONFIRM_ACTION"),
        closeText: this._getLabel("CANCEL"),
        callback: () => this.confirmFinalize(),
      });
    },
    hasMeaningfulValue(value) {
      if (typeof value === 'string') return !!value.trim();
      if (typeof value === 'number') return Number.isFinite(value);
      if (typeof value === 'boolean') return true;
      if (Array.isArray(value)) return value.some(item => this.hasMeaningfulValue(item));
      if (!value || typeof value !== 'object') return false;
      const ignored = ['id', 'patientId', 'encounterId', 'doctorId', 'organizationId', 'revision', 'contentRevision', 'createdAt', 'lastUpdate'];
      return Object.keys(value).some(key => !ignored.includes(key) && this.hasMeaningfulValue(value[key]));
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    getFinalizationReviewText() {
      const diagnoses = (this.localDiagnoses || []).filter(item => item && !item.deleted && (item.name || '').trim());
      const prescriptionItems = (this.prescription || []).reduce((total, page) => total + ((page || []).length), 0);
      const sections = [
        { name: 'anamnese', filled: this.hasMeaningfulValue({
          chiefComplaint: this.localEncounter.chiefComplaint,
          notes: this.localEncounter.notes,
          details: this.localAnamnesis,
        }) },
        { name: 'exame físico', filled: this.hasMeaningfulValue(this.localPhysicalExam) },
        { name: 'diagnósticos', filled: diagnoses.length > 0 },
        { name: 'receita atualmente aberta', filled: prescriptionItems > 0 },
      ];
      const filled = sections.filter(section => section.filled).map(section => section.name);
      const empty = sections.filter(section => !section.filled).map(section => section.name);
      const summary = `Registrado nesta tela: ${filled.length ? filled.join(', ') : 'nenhuma seção clínica'}; ${diagnoses.length} diagnóstico(s) e ${prescriptionItems} item(ns) na receita atualmente aberta.`;
      const pending = empty.length ? `Sem conteúdo nesta tela: ${empty.join(', ')}. Revise se isso é esperado.` : 'Todas as seções desta tela possuem conteúdo.';
      const patientName = this.escapeHtml((this.patient && this.patient.name) || '-');
      const date = this.escapeHtml(this.formattedDate || '-');
      return `${this._getLabel("FINALIZE_ENCOUNTER_CONFIRM")}<br><br><strong>Revisão antes de finalizar</strong><br>Paciente: ${patientName}<br>Data da consulta: ${date}<br>${summary}<br>${pending}`;
    },
    async confirmFinalize() {
      const saved = await this.saveAll({
        alertSuffix: this._getLabel("ENCOUNTER_FINALIZE_WITHOUT_SAVE"),
      });
      if (!saved.ok) return;
      this.saving = true;
      try {
        const result = await this.saveEncounter({
          encounter: this.buildEncounterPayload({ finalize: true }),
          showMessage: false,
          showError: false,
        });
        if (result && result.ok) {
          this.showAlert({
            text: this._getLabel("ENCOUNTER_FINALIZED_SUCCESS"),
            color: "success",
          });
          this.$nextTick(() => {
            this._setupScreenSizes(this);
          });
          return;
        }
        this.showAlert({
          text: this.getFinalizeErrorMessage(result && result.error),
          color: "error",
        });
      } finally {
        this.saving = false;
      }
    },
    /**
     * O backend responde `{ message, payload }`; no 400 a mensagem diz o que
     * falta (conteudo clinico minimo, data invalida) e repetir a acao sem ela
     * nunca resolve.
     */
    getServerMessage(error) {
      const data = error && error.response ? error.response.data : null;
      const message = data && typeof data.message === "string" ? data.message.trim() : "";
      return message || "";
    },
    getFinalizeErrorMessage(error) {
      const status = error && error.response ? error.response.status : null;
      if (status === 403) return this._getLabel("ENCOUNTER_FINALIZE_FORBIDDEN");
      if (status === 409) return this._getLabel("ENCOUNTER_FINALIZE_CONFLICT");
      if (status === 400) {
        return (
          this.getServerMessage(error) ||
          this._getLabel("ENCOUNTER_FINALIZE_ERROR")
        );
      }
      return this._getLabel("ENCOUNTER_FINALIZE_ERROR");
    },
    getAddendumSignature(item) {
      const author = (item && item.author) || {};
      const name = [author.displayNamePrefix, author.displayName]
        .filter((part) => !!part)
        .join(" ");
      const date = item && item.createdAt
        ? new Date(item.createdAt).toLocaleString("pt-BR")
        : "";
      return [name, date].filter((part) => !!part).join(" • ");
    },
    openAddendumDialog() {
      this.addendumText = "";
      this.addendumDialog = true;
    },
    closeAddendumDialog() {
      this.addendumDialog = false;
      this.addendumText = "";
    },
    async submitAddendum() {
      const text = (this.addendumText || "").trim();
      if (!text) return;
      this.savingAddendum = true;
      try {
        const result = await this.addEncounterAddendum({
          encounterId: this.routeEncounterId,
          text,
          showError: false,
        });
        if (result && result.ok) {
          this.closeAddendumDialog();
          this.showAlert({
            text: this._getLabel("ADDENDUM_SAVED"),
            color: "success",
          });
          this.$nextTick(() => {
            this._setupScreenSizes(this);
          });
          return;
        }
        this.showAlert({
          text: this._getLabel("ADDENDUM_ERROR"),
          color: "error",
        });
      } finally {
        this.savingAddendum = false;
      }
    },
    /**
     * Resolve o paciente vindo da URL (refresh na rota direta).
     * Devolve false quando 403/404 ja redirecionou a tela.
     */
    async resolvePatientFromRoute() {
      const routePatientId = this.routePatientId;
      if (!routePatientId) return true;
      if (this.patient && this.patient.id === routePatientId) return true;
      const result = await this.selectPatientById(routePatientId);
      if (result && result.ok) return true;
      const status = result ? result.status : null;
      this.showAlert({
        text:
          status === 403
            ? this._getLabel("PATIENT_ACCESS_DENIED")
            : this._getLabel("PATIENT_NOT_FOUND"),
        color: "error",
      });
      this.contextError = 'Paciente indisponível. Volte à lista de pacientes para continuar.';
      return false;
    },
  },
  watch: {
    draftContents: {
      deep: true,
      handler() {
        this.scheduleDraftPersistence();
      },
    },
    anamnesis: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.localAnamnesis)) {
          this.localAnamnesis = { ...(val || {}) };
        }
      },
      deep: true,
    },
    encounter: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.localEncounter)) {
          this.localEncounter = { ...(val || {}) };
        }
      },
      deep: true,
    },
    physicalExam: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.localPhysicalExam)) {
          this.localPhysicalExam = { ...(val || {}) };
        }
      },
      deep: true,
    },
    diagnoses: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.localDiagnoses)) {
          this.localDiagnoses = [...(val || [])];
        }
      },
      deep: true,
    },
    patient() {
      this.$nextTick(() => {
        this._setupScreenSizes(this);
      });
    },
    encounterTab() {
      this.$nextTick(() => {
        this._setupScreenSizes(this);
      });
    },
    isFinished() {
      this.$nextTick(() => {
        this._setupScreenSizes(this);
      });
    },
  },
  beforeRouteLeave(to, from, next) {
    this.requestLeave(next);
  },
  beforeRouteUpdate(to, from, next) {
    this.requestLeave(allowed => {
      next(allowed);
      if (allowed !== false) this.$nextTick(() => this.loadContext());
    });
  },
  beforeDestroy() {
    ++this.contextSequence;
    this.cancelDraftTimer();
    window.removeEventListener('pagehide', this.flushDraft);
    window.removeEventListener('vetalfa:logout', this.handleLogout);
    window.removeEventListener('vetalfa:logout-cancelled', this.handleLogoutCancelled);
    document.removeEventListener('pause', this.flushDraft);
  },
  async mounted() {
    if (!this.canAccessRoute) {
      return this.routeRedirect({ name: "home" });
    }
    window.addEventListener('pagehide', this.flushDraft);
    window.addEventListener('vetalfa:logout', this.handleLogout);
    window.addEventListener('vetalfa:logout-cancelled', this.handleLogoutCancelled);
    document.addEventListener('pause', this.flushDraft);
    await this.loadContext();
  },
};
</script>
<style scoped>
.clinical-encounter-actions {
  bottom: 0 !important;
  padding-top: 12px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid #e6eaf0;
  box-shadow: 0 -4px 20px rgba(25, 49, 83, .04);
}
.clinical-encounter-desktop-actions {
  gap: 12px;
  min-height: 48px;
}
.clinical-encounter-desktop-actions .v-btn {
  min-height: 40px;
}
@media (min-width: 960px) {
  .clinical-encounter-tabs,
  .clinical-encounter-desktop-actions {
    padding-bottom: 8px !important;
    padding-top: 8px !important;
  }
  .clinical-encounter-desktop-actions {
    padding-left: 24px !important;
  }
}
.clinical-read-block p { white-space: pre-wrap; color: #193153; margin-bottom: 18px; }
.clinical-read-block p span { display: block; color: #68788a; font-size: 12px; margin-bottom: 4px; }
</style>
